/**
 * API Service - Cliente HTTP para o backend
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import type {
  Investigation,
  CreateInvestigationPayload,
  ListInvestigationsResponse
} from '../types/investigation';

// Base URL da API (lê do .env ou usa default)
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.commanda.mobi';

/**
 * Instância do Axios configurada
 */
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 segundos
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Interceptor de Request - Adiciona logs (desenvolvimento)
 */
api.interceptors.request.use(
  (config) => {
    // Log apenas em desenvolvimento
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

/**
 * Interceptor de Response - Tratamento de erros
 */
api.interceptors.response.use(
  (response) => {
    // Log de sucesso em desenvolvimento
    if (import.meta.env.DEV) {
      console.log(`[API Response] ${response.status} ${response.config.url}`);
    }
    return response;
  },
  (error: AxiosError) => {
    // Tratamento de erros
    if (error.response) {
      // Servidor respondeu com status de erro
      console.error(
        `[API Error] ${error.response.status} - ${error.response.statusText}`,
        error.response.data
      );

      // Erro específico de CORS
      if (error.message?.includes('CORS')) {
        console.error('[CORS Error] Verifique configuração de CORS no backend');
      }
    } else if (error.request) {
      // Requisição foi feita mas sem resposta
      console.error('[API Error] Sem resposta do servidor', error.request);
    } else {
      // Erro ao configurar a requisição
      console.error('[API Error] Erro ao fazer requisição', error.message);
    }

    return Promise.reject(error);
  }
);

/**
 * Interface de erro padronizada
 */
export interface ApiError {
  message: string;
  status?: number;
  data?: any;
}

/**
 * Função helper para tratar erros
 */
export const handleApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    return {
      message: error.response?.data?.message || error.message || 'Erro desconhecido',
      status: error.response?.status,
      data: error.response?.data,
    };
  }

  return {
    message: error instanceof Error ? error.message : 'Erro desconhecido',
  };
};

/**
 * API de Investigações
 */
export const investigacaoAPI = {
  /**
   * Listar todas as investigações
   */
  listar: async (): Promise<Investigation[]> => {
    try {
      const response = await api.get<Investigation[]>('/investigacao');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Buscar investigação por ID
   */
  buscar: async (id: string): Promise<Investigation> => {
    try {
      const response = await api.get<Investigation>(`/investigacao/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Criar nova investigação
   */
  iniciar: async (payload: CreateInvestigationPayload): Promise<Investigation> => {
    try {
      const response = await api.post<Investigation>('/investigacao/iniciar', payload);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Expandir um nó da investigação
   */
  expandir: async (investigacaoId: string, noId: string): Promise<Investigation> => {
    try {
      const response = await api.post<Investigation>(
        `/investigacao/${investigacaoId}/expandir/${noId}`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Deletar investigação
   */
  deletar: async (id: string): Promise<void> => {
    try {
      await api.delete(`/investigacao/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

/**
 * Export default do cliente HTTP (caso necessário usar diretamente)
 */
export default api;
